/**
 * Tia Knowledge Base — Figma popup inside the assistant panel.
 * Same org bases as Omni (`knowledgeBaseApi`); create/edit/toggle/content
 * land in both places via the shared query cache.
 *
 * Create opens inline in this panel (not an overlay dialog). Content add
 * flows (file/FAQ/text/URL) keep their existing overlay dialogs.
 */
import { useRef, useState } from 'react';
import { Form, Formik } from 'formik';
import {
  Add,
  ArrowLeft,
  CloseSquare,
  DocumentText,
  GlobalSearch,
  MessageQuestion,
  SearchNormal1,
  TextalignJustifyright,
} from 'iconsax-react';
import type { Icon } from 'iconsax-react';
import { Loader2 } from 'lucide-react';

import { Button } from '@design-system/button';
import { cn } from '@design-system/helpers';
import { Switch } from '@design-system/switch';
import { Tabs, TabsList, TabsTrigger } from '@design-system/tabs';
import { Textfield } from '@shared/fields/text-field';
import { TextAreafield } from '@shared/fields/text-area-field';
import { nearEdge } from '@utils/near-edge';
import { useFillViewport } from '@hooks/use-fill-viewport';

import { DocumentsTab } from '@features/knowledge-base/components/documents-tab';
import { FaqsTab } from '@features/knowledge-base/components/faqs-tab';
import {
  KbaseActiveField,
  KbaseAgentsField,
  kbaseFormSchema,
} from '@features/knowledge-base/components/kbase-form-dialog';
import { KbaseAvatar } from '@features/knowledge-base/components/kbase-row';
import { RichTextsTab } from '@features/knowledge-base/components/rich-texts-tab';
import { WebsitesTab } from '@features/knowledge-base/components/websites-tab';
import emptyIllustration from '@features/knowledge-base/assets/kbase-empty-illustration.png';
import type { KnowledgeTab } from '@features/knowledge-base/hooks/use-knowledge-base-filters';
import {
  knowledgeBaseApi,
  type KnowledgeBase,
} from '@features/knowledge-base/services/knowledge-base-api';
import { agentsLabel } from '@features/knowledge-base/utils/labels';

const LOAD_MORE_PX = 200;

const TABS: ReadonlyArray<{
  tab: KnowledgeTab;
  label: string;
  icon: Icon;
  count: (kbase: KnowledgeBase) => number;
}> = [
  {
    tab: 'files',
    label: 'Files',
    icon: DocumentText,
    count: (k) => k.document_count,
  },
  {
    tab: 'texts',
    label: 'Rich Texts',
    icon: TextalignJustifyright,
    count: (k) => k.rich_text_count,
  },
  {
    tab: 'faqs',
    label: 'FAQs',
    icon: MessageQuestion,
    count: (k) => k.faq_count,
  },
  {
    tab: 'urls',
    label: 'Web Crawler',
    icon: GlobalSearch,
    count: (k) => k.website_count,
  },
];

const ICON_BUTTON =
  'shrink-0 rounded-lg p-0 text-gray-1000 hover:bg-primary-50';

type PanelScreen =
  | { kind: 'list' }
  | { kind: 'create' }
  | { kind: 'detail'; id: number };

function KnowledgeHeader({
  title,
  onBack,
  onAdd,
  onClose,
  showAdd = true,
}: {
  title: string;
  onBack: () => void;
  onAdd?: () => void;
  onClose: () => void;
  showAdd?: boolean;
}) {
  return (
    <div
      data-slot='tia-knowledge-header'
      className='flex h-[69px] shrink-0 items-center gap-2 border-b border-primary-200 px-4'
    >
      <button
        type='button'
        aria-label='Back'
        onClick={onBack}
        className={ICON_BUTTON}
      >
        <ArrowLeft
          size={24}
          className='size-6'
        />
      </button>
      <p className='min-w-0 flex-1 truncate text-center text-base font-semibold capitalize leading-[1.2] text-gray-1000'>
        {title}
      </p>
      {showAdd ? (
        <button
          type='button'
          aria-label='Add knowledge base'
          onClick={onAdd}
          className={ICON_BUTTON}
        >
          <Add
            size={24}
            className='size-6'
          />
        </button>
      ) : (
        <span
          className='size-6 shrink-0'
          aria-hidden='true'
        />
      )}
      <button
        type='button'
        aria-label='Close'
        onClick={onClose}
        className={ICON_BUTTON}
      >
        <CloseSquare
          size={24}
          className='size-6'
        />
      </button>
    </div>
  );
}

function EmptyState({ onCreate }: { onCreate: () => void }) {
  return (
    <div
      data-slot='tia-knowledge-empty'
      className='flex flex-1 flex-col items-center justify-center gap-2 px-6 py-10'
    >
      <img
        src={emptyIllustration}
        alt=''
        width={205}
        height={180}
        className='h-[180px] w-[205px] object-contain'
      />
      <p className='text-center text-lg font-medium text-gray-800'>
        No Knowledge Base Yet
      </p>
      <Button
        type='button'
        variant='primary'
        size='lg'
        className='mt-2 h-12 rounded-xl px-5 [&_svg]:size-5'
        onClick={onCreate}
      >
        <Add
          size={20}
          aria-hidden='true'
        />
        Add Knowledge Base
      </Button>
    </div>
  );
}

function KnowledgeCard({
  kbase,
  onOpen,
}: {
  kbase: KnowledgeBase;
  onOpen: (id: number) => void;
}) {
  const update = knowledgeBaseApi.useUpdate();

  return (
    <div
      role='option'
      tabIndex={0}
      data-slot='tia-knowledge-card'
      data-kbase-id={kbase.id}
      aria-label={kbase.name}
      onClick={() => onOpen(kbase.id)}
      onKeyDown={(event) => {
        if (event.target !== event.currentTarget) return;
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onOpen(kbase.id);
        }
      }}
      className='flex cursor-pointer items-center gap-2 rounded-xl border border-primary-200 px-3 py-2 transition-colors hover:bg-primary-30'
    >
      <div className='flex min-w-0 flex-1 items-center gap-2 overflow-hidden py-3'>
        <KbaseAvatar
          active={kbase.is_active}
          className='size-10'
        />
        <div className='flex min-w-0 flex-1 flex-col gap-1'>
          <span className='truncate text-base font-medium text-gray-900'>
            {kbase.name}
          </span>
          <span className='flex items-center gap-1 text-sm italic text-gray-600'>
            <span className='truncate'>
              {agentsLabel(kbase.ai_agents.length)}
            </span>
            <span aria-hidden='true'>•</span>
            <span
              className={kbase.is_active ? 'text-[#00b637]' : 'text-red-500'}
            >
              {kbase.is_active ? 'Active' : 'Inactive'}
            </span>
          </span>
        </div>
      </div>
      <Switch
        checked={kbase.is_active}
        disabled={update.isPending}
        aria-label={
          kbase.is_active
            ? `Deactivate ${kbase.name}`
            : `Activate ${kbase.name}`
        }
        onClick={(event) => event.stopPropagation()}
        onCheckedChange={(checked) =>
          update.mutate({
            id: kbase.id,
            name: kbase.name,
            description: kbase.description,
            is_active: checked,
            ai_agent_ids: kbase.ai_agents.map((agent) => agent.id),
          })
        }
      />
    </div>
  );
}

function KnowledgeList({
  onSelect,
  onCreate,
}: {
  onSelect: (id: number) => void;
  onCreate: () => void;
}) {
  const [query, setQuery] = useState('');
  const list = knowledgeBaseApi.useFetchInfinite({
    ...(query.trim() && { q: query.trim() }),
    o: ['-updated_at'],
    page_size: 25,
  });
  const rows = list.data?.pages.flatMap((page) => page.results) ?? [];
  const scrollRef = useRef<HTMLDivElement>(null);
  const loadMore = () => void list.fetchNextPage();
  useFillViewport(
    scrollRef,
    list.hasNextPage,
    list.isFetchingNextPage,
    loadMore,
    rows.length
  );

  const showEmpty = !list.isPending && !list.isError && rows.length === 0;
  const isBlankEmpty = showEmpty && !query.trim();

  return (
    <div
      data-slot='tia-knowledge-list'
      className='flex min-h-0 flex-1 flex-col gap-2 overflow-hidden p-4'
    >
      <div className='flex shrink-0 items-center gap-2'>
        <div className='flex h-10 min-w-0 flex-1 items-center gap-2 rounded-xl border border-primary-200 bg-primary-30 px-3'>
          <SearchNormal1
            size={16}
            className='size-4 shrink-0 text-gray-800'
            aria-hidden='true'
          />
          <input
            type='search'
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder='Search in knowledge base'
            aria-label='Search knowledge bases'
            className='min-w-0 flex-1 bg-transparent text-sm text-gray-800 placeholder:text-gray-600 focus:outline-none'
          />
        </div>
        <Button
          type='button'
          variant='primaryPale'
          size='sm'
          className='h-10 shrink-0 rounded-xl border-0 bg-primary-100 px-3 text-primary-1000 hover:bg-primary-100/80 [&_svg]:size-5'
          onClick={onCreate}
        >
          <Add aria-hidden='true' />
          Add Base
        </Button>
      </div>

      {list.isPending && (
        <div className='flex flex-1 items-center justify-center py-10'>
          <Loader2
            className='animate-spin text-primary-1000'
            size={22}
          />
        </div>
      )}
      {list.isError && (
        <p
          role='alert'
          className='rounded-[10px] bg-red-100 px-3 py-1.5 text-xs text-red-500'
        >
          Couldn&apos;t load your knowledge bases. Please try again.
        </p>
      )}
      {isBlankEmpty ? (
        <EmptyState onCreate={onCreate} />
      ) : (
        <div
          ref={scrollRef}
          className='flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto'
          onScroll={(event) => {
            if (!list.hasNextPage || list.isFetchingNextPage) return;
            if (nearEdge(event.currentTarget, 'bottom', LOAD_MORE_PX))
              loadMore();
          }}
        >
          {!showEmpty && (
            <p className='shrink-0 text-sm font-medium capitalize text-gray-600'>
              Channels Knowledge Base
            </p>
          )}
          {showEmpty && query.trim() ? (
            <p className='mt-8 text-center text-sm text-gray-500'>
              No knowledge bases match your search.
            </p>
          ) : (
            rows.map((kbase) => (
              <KnowledgeCard
                key={kbase.id}
                kbase={kbase}
                onOpen={onSelect}
              />
            ))
          )}
          {list.isFetchingNextPage && (
            <div className='flex justify-center py-3'>
              <Loader2
                className='animate-spin text-primary-1000'
                size={18}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** Inline create form — stays inside the Tia panel (Figma New Knowledge Base). */
function KnowledgeCreateForm({
  onBack,
  onClose,
  onCreated,
}: {
  onBack: () => void;
  onClose: () => void;
  onCreated: (saved: KnowledgeBase) => void;
}) {
  const create = knowledgeBaseApi.useCreate();

  return (
    <div
      data-slot='tia-knowledge-create'
      className='flex min-h-0 flex-1 flex-col overflow-hidden'
    >
      <KnowledgeHeader
        title='New Knowledge Base'
        onBack={onBack}
        onClose={onClose}
        showAdd={false}
      />
      <Formik
        initialValues={{
          name: '',
          description: '',
          is_active: true,
          ai_agent_ids: [] as number[],
        }}
        validationSchema={kbaseFormSchema}
        onSubmit={async (values) => {
          const saved = await create.mutateAsync({
            name: values.name.trim(),
            description: values.description.trim() || null,
            is_active: values.is_active,
            ai_agent_ids: values.ai_agent_ids,
          });
          onCreated(saved);
        }}
      >
        {({ submitForm }) => (
          <Form className='flex min-h-0 flex-1 flex-col overflow-hidden'>
            <div className='flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4'>
              <Textfield
                name='name'
                label='Knowledge Base Name'
                placeholder='Enter knowledge base name'
              />
              <TextAreafield
                name='description'
                label='Description'
                placeholder='e.g. What this base covers…'
                withMaxLength={false}
                rows={5}
                labelProps={{ className: 'mb-0' }}
              />
              <KbaseAgentsField />
              <KbaseActiveField />
            </div>
            <div className='flex shrink-0 gap-4 border-t border-primary-100 p-4 shadow-[0px_-2px_4px_rgba(0,0,0,0.06)]'>
              <Button
                type='button'
                variant='primary'
                className='h-12 flex-1 rounded-xl'
                isLoading={create.isPending}
                onClick={submitForm}
              >
                Continue
              </Button>
              <Button
                type='button'
                variant='primaryOutline'
                className='h-12 flex-1 rounded-xl border-primary-200 text-primary-1000'
                onClick={onBack}
              >
                Cancel
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
}

function KnowledgeDetail({
  kbaseId,
  onBack,
  onClose,
}: {
  kbaseId: number;
  onBack: () => void;
  onClose: () => void;
}) {
  const [tab, setTab] = useState<KnowledgeTab>('files');
  const selected = knowledgeBaseApi.useFetchOne({ id: kbaseId });
  const kbase = selected.data;

  return (
    <div
      data-slot='tia-knowledge-detail'
      className='flex min-h-0 flex-1 flex-col overflow-hidden'
    >
      <KnowledgeHeader
        title={kbase?.name ?? 'Knowledge Base'}
        onBack={onBack}
        onClose={onClose}
        showAdd={false}
      />
      {selected.isError ? (
        <p
          role='alert'
          className='m-4 rounded-[10px] bg-red-100 px-3 py-1.5 text-xs text-red-500'
        >
          Couldn&apos;t load this knowledge base. It may have been deleted.
        </p>
      ) : !kbase ? (
        <div className='flex flex-1 items-center justify-center'>
          <Loader2
            className='animate-spin text-primary-1000'
            size={22}
          />
        </div>
      ) : (
        <Tabs
          value={tab}
          onValueChange={(value) => setTab(value as KnowledgeTab)}
          className='flex min-h-0 flex-1 flex-col overflow-hidden'
        >
          <TabsList className='h-fit shrink-0 justify-start gap-0 overflow-x-auto border-b border-primary-200 px-0 pb-0'>
            {TABS.map((entry) => {
              const TabIcon = entry.icon;
              const active = tab === entry.tab;
              return (
                <TabsTrigger
                  key={entry.tab}
                  value={entry.tab}
                  className={cn(
                    'gap-2 rounded-none border-0 border-b-2 border-transparent px-3 py-3 text-[13px] font-medium whitespace-nowrap shadow-none',
                    active
                      ? 'border-b-primary-1000 text-primary-1000'
                      : 'text-gray-600'
                  )}
                >
                  <TabIcon
                    size={20}
                    variant={active ? 'Bold' : 'Linear'}
                    aria-hidden='true'
                  />
                  {entry.label}
                </TabsTrigger>
              );
            })}
          </TabsList>
          <div className='flex min-h-0 flex-1 flex-col overflow-hidden'>
            {tab === 'files' && (
              <DocumentsTab
                key={kbase.id}
                kbaseId={kbase.id}
                softActions
              />
            )}
            {tab === 'texts' && (
              <RichTextsTab
                key={kbase.id}
                kbaseId={kbase.id}
                softActions
              />
            )}
            {tab === 'faqs' && (
              <FaqsTab
                key={kbase.id}
                kbaseId={kbase.id}
                softActions
              />
            )}
            {tab === 'urls' && (
              <WebsitesTab
                key={kbase.id}
                kbaseId={kbase.id}
                softActions
              />
            )}
          </div>
        </Tabs>
      )}
    </div>
  );
}

export function TiaKnowledgeBasePanel({
  onBack,
  onClose,
}: {
  onBack: () => void;
  onClose: () => void;
}) {
  const [screen, setScreen] = useState<PanelScreen>({ kind: 'list' });

  if (screen.kind === 'create') {
    return (
      <KnowledgeCreateForm
        onBack={() => setScreen({ kind: 'list' })}
        onClose={onClose}
        onCreated={(saved) => setScreen({ kind: 'detail', id: saved.id })}
      />
    );
  }

  if (screen.kind === 'detail') {
    return (
      <KnowledgeDetail
        kbaseId={screen.id}
        onBack={() => setScreen({ kind: 'list' })}
        onClose={onClose}
      />
    );
  }

  return (
    <div
      data-slot='tia-knowledge-base-panel'
      className='flex h-full min-h-0 flex-col overflow-hidden'
    >
      <KnowledgeHeader
        title='Knowledge Base'
        onBack={onBack}
        onAdd={() => setScreen({ kind: 'create' })}
        onClose={onClose}
      />
      <KnowledgeList
        onSelect={(id) => setScreen({ kind: 'detail', id })}
        onCreate={() => setScreen({ kind: 'create' })}
      />
    </div>
  );
}
